<template>
  <div>
    <div class="row mb-3">
      <input
        type="text"
        v-model="searchQuery"
        :placeholder="$t ? $t('Search') : 'Search'"
        @input="onSearchInput"
        @keydown.enter.prevent
        class="search-input"
      />
      <button
        type="button"
        @click="detectLocation"
        class="icon-btn"
        title="Detect Location"
      >
        <font-awesome-icon icon="crosshairs" />
      </button>

      <button
        type="button"
        @click="openMapModal"
        class="icon-btn"
        title="Open Map"
      >
        <font-awesome-icon icon="location-dot" />
      </button>
      <button
        type="button"
        @click="clearSearch"
        class="icon-btn"
        title="Clear Search"
      >
        X
      </button>
    </div>

    <ul
      v-if="autocompleteResults.length"
      class="border max-h-48 overflow-auto rounded-md mb-3"
    >
      <li
        v-for="(result, idx) in autocompleteResults"
        :key="idx"
        @click="selectLocation(result)"
        class="p-2 hover:bg-gray-200 cursor-pointer"
      >
        {{ result.display_name }}
      </li>
    </ul>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <button
          @click="closeModal"
          class="self-end mb-2 px-3 py-1 bg-red-500 rounded"
        >
          {{ $t('Close') }}
        </button>

        <LMap
          v-if="showModal"
          :key="mapKey"
          style="width: 100%; height: 400px;"
          :zoom="zoom"
          :center="center"
          @update:center="updateCenter"
          @click="onMapClick"
          ref="mapRef"
        >
          <LTileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          <LMarker
            :lat-lng="markerPosition"
            :draggable="true"
            @update:lat-lng="updateMarkerPosition"
          />
        </LMap>

        <button
          type="button"
          @click="confirmMapSelection"
          class="mt-3 bg-blue-600  p-2 rounded w-full"
        >
          {{ $t('Confirm Location') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global google */
import { ref, watch, defineEmits, defineProps, onMounted, nextTick } from "vue";
import { LMap, LTileLayer, LMarker } from "vue3-leaflet";

const emit = defineEmits(["update:location"]);

const props = defineProps({
  latitude: {
    type: [Number, String],
    default: null,
  },
  longitude: {
    type: [Number, String],
    default: null,
  },
  location: {
    type: Object,
    default: () => ({
      lat: 34.8021,
      lng: 38.9968,
      address: "",
    }),
  },
});
const mapKey = ref(0);

const mapRef = ref(null);

const defaultLat = 34.8021;
const defaultLng = 38.9968;

const zoom = ref(6);

const isValidCoordinate = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return false;
  }

  const number = Number(value);

  return Number.isFinite(number);
};

const getCurrentCoordinates = () => {
  if (
    isValidCoordinate(props.latitude) &&
    isValidCoordinate(props.longitude)
  ) {
    return [
      Number(props.latitude),
      Number(props.longitude)
    ];
  }

  if (
    isValidCoordinate(props.location?.lat) &&
    isValidCoordinate(props.location?.lng)
  ) {
    return [
      Number(props.location.lat),
      Number(props.location.lng)
    ];
  }

  return [defaultLat, defaultLng];
};

const center = ref(getCurrentCoordinates());

const markerPosition = ref([...center.value]);

const searchQuery = ref(props.location.address || "");
const autocompleteResults = ref([]);

const showModal = ref(false);

let autocompleteService = null;
let geocoder = null;

watch(
  () => [
    props.latitude,
    props.longitude,
    props.location?.lat,
    props.location?.lng
  ],
  () => {
    const position = getCurrentCoordinates();

    center.value = [...position];
    markerPosition.value = [...position];

    console.log('MapPicker coordinates:', {
      latitude: props.latitude,
      longitude: props.longitude,
      location: props.location,
      finalPosition: position
    });

    if (showModal.value && mapRef.value?.leafletObject) {
      mapRef.value.leafletObject.setView(
        position,
        6,
        {
          animate: false
        }
      );
    }
  },
  {
    immediate: true
  }
);

onMounted(() => {
  if (window.google && window.google.maps) {
    autocompleteService = new google.maps.places.AutocompleteService();
    geocoder = new google.maps.Geocoder();
  } else {
    console.error("Google Maps JS API not loaded");
  }
});

const updateCenter = (newCenter) => {
  center.value = newCenter;
};

const updateMarkerPosition = async (newPos) => {
  markerPosition.value = newPos;
  center.value = newPos;
  await reverseGeocode(newPos[0], newPos[1]);
};

const onSearchInput = () => {
  if (!autocompleteService) return;
  if (searchQuery.value.length < 3) {
    autocompleteResults.value = [];
    return;
  }

  autocompleteService.getPlacePredictions(
    { input: searchQuery.value },
    (predictions, status) => {
      if (
        status !== google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        autocompleteResults.value = [];
        return;
      }
      autocompleteResults.value = predictions.map((p) => ({
        placeId: p.place_id,
        display_name: p.description,
      }));
    }
  );
};

const selectLocation = (loc) => {
  if (!geocoder) return;

  geocoder.geocode({ placeId: loc.placeId }, (results, status) => {
    if (status === "OK" && results[0]) {
      const location = results[0].geometry.location;

      const lat = location.lat();
      const lng = location.lng();

      const position = [lat, lng];

      center.value = position;
      markerPosition.value = position;

      searchQuery.value = results[0].formatted_address;
      autocompleteResults.value = [];

      emit("update:location", {
        lat,
        lng,
        address: results[0].formatted_address,
      });

      showModal.value = true;

      nextTick(() => {
        setTimeout(() => {
          if (mapRef.value?.leafletObject) {
            mapRef.value.leafletObject.invalidateSize();
            mapRef.value.leafletObject.setView(position, 13, {
              animate: false,
            });
          }
        }, 100);
      });
    }
  });
};

const detectLocation = () => {
  if (!navigator.geolocation) {
    alert("Geolocation is not supported by your browser");
    return;
  }
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      await reverseGeocode(lat, lng);
      center.value = [lat, lng];
      markerPosition.value = [lat, lng];
      zoom.value = 6;
      showModal.value = true;
    },
    (error) => {
      alert("Unable to retrieve your location");
      console.error(error);
    }
  );
};
const reverseGeocode = (lat, lng) => {
  return new Promise((resolve) => {
    if (!geocoder) {
      resolve();
      return;
    }
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        searchQuery.value = results[0].formatted_address;
        emit("update:location", { lat, lng, address: results[0].formatted_address });
        resolve();
      } else {
        console.error("Reverse geocode failed:", status);
        resolve();
      }
    });
  });
};

const clearSearch = () => {
  searchQuery.value = "";
  autocompleteResults.value = [];
};

const onMapClick = async (event) => {
  const { lat, lng } = event.latlng;
  markerPosition.value = [lat, lng];
  center.value = [lat, lng];
  await reverseGeocode(lat, lng);
};

const confirmMapSelection = async () => {
  const lat = markerPosition.value[0];
  const lng = markerPosition.value[1];
  await reverseGeocode(lat, lng);
  showModal.value = false;
};

const openMapModal = async () => {
  const position = getCurrentCoordinates();

  const lat = position[0];
  const lng = position[1];

  console.log('OPEN MODAL AT:', {
    latitudeProp: props.latitude,
    longitudeProp: props.longitude,
    locationProp: props.location,
    finalLat: lat,
    finalLng: lng
  });

  center.value = [lat, lng];
  markerPosition.value = [lat, lng];
  zoom.value = 6;

  mapKey.value++;

  showModal.value = true;

  await nextTick();

  setTimeout(() => {
    const map = mapRef.value?.leafletObject;

    if (map) {
      map.invalidateSize();

      map.setView(
        [lat, lng],
        13,
        {
          animate: false
        }
      );
    }
  }, 100);
};
const closeModal = () => {
  showModal.value = false;
};
</script>

<style scoped>
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 3px;
}

.search-input {
  width: 50%;
  padding: 8px 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-sizing: border-box;
}

.icon-btn {
  width: 38px;
  height: 38px;
  background-color: #74bf44;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s ease;
}

.icon-btn:hover {
  background-color: #5ea232;
}

.border {
  border: 1px solid #ccc;
}

.max-h-48 {
  max-height: 12rem;
}

.overflow-auto {
  overflow: auto;
}

.rounded-md {
  border-radius: 6px;
}

.mb-3 {
  margin-bottom: 1rem;
}

.p-2 {
  padding: 0.5rem;
}

.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}

.cursor-pointer {
  cursor: pointer;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 1rem;
  border-radius: 10px;
  width: 90vw;
  max-width: 600px;
  height: 90vh;
  display: flex;
  flex-direction: column;
}
</style>
